import { Component } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { CampaignType, StrategyTactic, TacticType } from "@front/m19-api-client";
import { SegmentEx, StrategyEx } from "@front/m19-models";
import { AccountSelectionService, SdStrategiesService, StrategyService } from "@front/m19-services";
import { IButtonComponent, ModalService, ToasterService } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { StrategyOverlayComponent } from "@m19-board/overlay/strategy-overlay.component";
import {
  ProductSegmentModalComponent,
  ProductSegmentModalData,
} from "@m19-board/segments/product-segment-modal.component";
import { SdTargetingComponent } from "@m19-board/sponsored-display/sd-targeting/sd-targeting.component";
import { StrategyAsinsComponent } from "@m19-board/sponsored-product/sp-page/strategy-asins/strategy-asins.component";
import { StrategyPageComponent } from "@m19-board/strategies/strategy-page/strategy-page.component";
import { SegmentCreactionRequest } from "@m19-board/strategies/strategy-page/tactic-add-popup.component";
import { ICON_ADD } from "@m19-board/utils/iconsLabels";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest, Observable, Subscription } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import {
  SdTargetingAddPopupComponent,
  SdTargetingAddPopupData,
} from "../sd-targeting/sd-targeting-add-popup.component";

@Component({
  selector: "app-sd-page",
  templateUrl: "./sd-page.component.html",
  standalone: true,
  imports: [
    TranslocoDirective,
    StrategyOverlayComponent,
    StrategyPageComponent,
    SdTargetingComponent,
    IButtonComponent,
    StrategyAsinsComponent,
  ],
})
@UntilDestroy()
export class SdPageComponent {
  strategy$: Observable<StrategyEx>;
  strategy: StrategyEx | undefined;
  isReadOnly = false;
  isLastTargeting = false;
  brands: Set<string> = new Set();
  organizationId!: number;

  readonly ICON_ADD = ICON_ADD;
  readonly CampaignType = CampaignType;

  constructor(
    private route: ActivatedRoute,
    private modalService: ModalService,
    private toastrService: ToasterService,
    private accountSelectionService: AccountSelectionService,
    private sdStrategiesService: SdStrategiesService,
    private strategyService: StrategyService,
    private translocoService: TranslocoService,
  ) {
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(untilDestroyed(this)).subscribe((am) => {
      this.organizationId = am.resourceOrganizationId!;
    });
    this,
      accountSelectionService.readOnlyMode$.pipe(untilDestroyed(this)).subscribe((readOnly) => {
        this.isReadOnly = readOnly;
      });
    this.strategy$ = combineLatest([
      this.accountSelectionService.singleAccountMarketplaceSelection$,
      this.route.paramMap,
    ]).pipe(
      switchMap(([am, params]) => {
        const id = Number(params.get("id"));
        return this.sdStrategiesService
          .getSDStrategies(am.accountId, am.marketplace)
          .pipe(map((strategies) => new StrategyEx(strategies.get(id)!)));
      }),
    );
    this.strategy$.pipe(untilDestroyed(this)).subscribe((s) => {
      this.strategy = s;
      // disableOtherQueries == !autoAlgoExplorationEnabled
      const nbTargetings = this.strategy
        ? (this.strategy.disableOtherQueries ? 0 : 1) +
          this.strategy.tactics.filter((x) => x.tacticType == TacticType.LEGACY).length +
          this.strategy.audienceTargetings.length
        : 0;

      this.isLastTargeting = nbTargetings <= 1;
    });
  }

  filterBlacklist(tactics: Array<StrategyTactic>, isBlacklist: boolean) {
    return tactics.filter((x) =>
      isBlacklist ? x.tacticType === TacticType.BLACKLIST : x.tacticType !== TacticType.BLACKLIST,
    );
  }

  createTargeting() {
    if (!this.strategy) {
      return;
    }

    const modalRef = this.modalService.openModal<SdTargetingAddPopupData, SegmentCreactionRequest>(
      SdTargetingAddPopupComponent,
      {
        modalTitle: this.translocoService.translate("sd-targeting-add-popup.add_a_new_targeting"),
        data: {
          strategy: this.strategy,
          organizationId: this.organizationId,
        },
      },
    );

    const subscription = modalRef.afterClosed().subscribe((segmentCreationRequest) => {
      if (!segmentCreationRequest) {
        return;
      }
      const segmentCreationRef = this.modalService.openModal<ProductSegmentModalData, SegmentEx>(
        ProductSegmentModalComponent,
        {
          modalTitle: this.translocoService.translate("product-segment-modal.product_segment"),
          data: {
            accountId: this.strategy!.accountId,
            marketplace: this.strategy!.marketplace,
          },
        },
      );
      this.createTacticAfterSegmentCreation(
        subscription,
        segmentCreationRequest.tacticType,
        segmentCreationRef.afterClosed(),
        segmentCreationRef.afterCancelled(),
      );
    });
  }

  private createTacticAfterSegmentCreation(
    subscription: Subscription,
    tacticType: TacticType,
    segmentCreated: Observable<SegmentEx>,
    segmentCreationCancelled: Observable<void>,
  ) {
    if (!this.strategy) {
      return;
    }
    subscription.add(
      segmentCreated
        .pipe(
          untilDestroyed(this),
          switchMap((s) => {
            return this.strategyService.addTacticToStrategy(
              this.strategy!.accountId,
              this.strategy!.marketplace,
              this.strategy!.strategyId!,
              s.segmentId,
              tacticType,
            );
          }),
        )
        .subscribe({
          next: () => {
            this.toastrService.success(
              this.translocoService.translate("sd-page.targeting_sucessfully_added_to_strategy"),
            );
            subscription.unsubscribe();
          },
          error: (error) => {
            this.toastrService.error(error, this.translocoService.translate("sd-page.targeting_creation_error"));
            subscription.unsubscribe();
          },
        }),
    );
    subscription.add(segmentCreationCancelled.pipe(untilDestroyed(this)).subscribe(() => subscription.unsubscribe()));
  }
}
